import { Component, inject, OnInit } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogContent,
  MatDialogActions,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import {
  ReactiveFormsModule,
  FormGroup,
  FormControl,
  Validators,
  AbstractControl,
  FormBuilder,
} from '@angular/forms';
import { AlertService } from '../../../_services/alert.service';
import { UserService } from '../../../_services/users.service';
import { TrimInputDirective } from '../../../../Shared/_directives/trim-input.directive';
import { UserCreateInput, UserUpdateInput } from '../../../_interfaces/users';
import { userRoles, userTypes } from '../../../../Shared/_constants/constants';

import { MatTooltipModule } from '@angular/material/tooltip';
import { SetPasswordComponent } from '../../../../Shared/_components/set-password/set-password.component';

@Component({
    selector: 'app-add-edit-customer',
    imports: [
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    ReactiveFormsModule,
    MatTooltipModule,
    TrimInputDirective,
    SetPasswordComponent
],
    templateUrl: './add-edit-customer.component.html',
    styleUrl: './add-edit-customer.component.css'
})
export class AddEditCustomerComponent implements OnInit {

  readonly dialogRef = inject(MatDialogRef<AddEditCustomerComponent>);
  readonly data = inject<any>(MAT_DIALOG_DATA);
  userInfo: any;

  get isEdit(): boolean {
    return !!(this.data && this.data.customer);
  }

  // Setting a membership tier is for admins, sales admins and super admins.
  get canSetType(): boolean {
    const roles = String(this.userInfo?.userrole ?? '').split(',').map((r: string) => r.trim());
    return ['ADMIN', 'SALEADMIN', 'S-ADMIN'].some((r) => roles.includes(r));
  }

  createCustomerForm: FormGroup = new FormGroup({
    useremail: new FormControl(''),
    firstname: new FormControl(''),
    lastname: new FormControl(''),
    displayname: new FormControl(''),
    vatnumber: new FormControl(''),
    phone: new FormControl(''),
    usertype: new FormControl(userTypes.NONMEMBER),
  });

  constructor(
    private formBuilder: FormBuilder,
    private alertService: AlertService,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    this.createCustomerForm = this.formBuilder.group({
      useremail: new FormControl('', [Validators.required, Validators.email]),
      firstname: new FormControl('', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(200),
      ]),
      lastname: new FormControl('', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(200),
      ]),
      displayname: new FormControl('', [
        Validators.minLength(2),
        Validators.maxLength(300),
      ]),
      vatnumber: new FormControl('', []),
      phone: new FormControl('', [
        // UK and international numbers: digits with spaces, +, brackets or dashes.
        Validators.pattern('^\\+?[0-9 ()-]{7,20}$'),
      ]),
      usertype: new FormControl(userTypes.NONMEMBER, [] ),
    });
    
    if (this.data && this.data.customer) {
      this.createCustomerForm.setValue({
        useremail: this.data.customer.useremail.toLowerCase(),
        firstname: this.data.customer.firstname,
        lastname: this.data.customer.lastname,
        displayname: this.data.customer.displayname ? this.data.customer.displayname : '' ,
        vatnumber: this.data.customer.vatnumber ? this.data.customer.vatnumber : '' ,
        phone: this.data.customer.phone ? this.data.customer.phone : '' ,
        usertype: this.data.customer.usertype? this.data.customer.usertype : ''
      });
    }
    this.initializeUser();
  }

  private async initializeUser() {
    this.userInfo = this.userService.isLoggedIn();
  }


  get f(): { [key: string]: AbstractControl } {
    return this.createCustomerForm.controls;
  }

  async onSubmit() {
    
    if (this.createCustomerForm.invalid) {
      this.alertService.alert('Please provide valid information', 'error');
      return;
    }

    if (this.data && this.data.customer) {
      this.updateExistingCustomer();
    }else {
      this.addNewCustomer();
    }

  }

  private async updateExistingCustomer() {
    const formData = this.createCustomerForm.value;

    const inputParams: UserUpdateInput = {
      firstname: formData.firstname.trim(),
      lastname: formData.lastname.trim(),
      id: this.data.customer.userid
    };

    if (formData.displayname) inputParams.displayname = formData.displayname.trim();
    if (formData.vatnumber) inputParams.vatnumber = formData.vatnumber.trim();
    if (formData.phone) inputParams.phone = formData.phone.trim();
    if (formData.usertype) inputParams.usertype = formData.usertype;
    if  (formData.usertype && formData.usertype !== 'NONMEMBER') 
      inputParams.userrole = userRoles.TRADER;
    else
      inputParams.userrole = userRoles.CUSTOMER;

    await this.userService
      .updateUser(inputParams)
      .subscribe({
        next: (response) => {
          this.alertService.alert("Customer updated successfully!", 'success');
          this.dialogRef.close({
            status: true,
          });
        },
        error: (error) => {
          this.alertService.alert(error.error.message, 'error');
          this.dialogRef.close({
            status: false,
          });
        },
      });


  }

  private async addNewCustomer() {
    const formData = this.createCustomerForm.value;

    const inputParams: UserCreateInput = {
      useremail: formData.useremail.trim().toLowerCase(),
      userrole: userRoles.CUSTOMER,
      usertype: userTypes.NONMEMBER,
      firstname: formData.firstname.trim(),
      lastname: formData.lastname.trim(),
      subscribe: true,
    };

    if (formData.displayname) inputParams.displayname = formData.displayname.trim();
    if (formData.vatnumber) inputParams.vatnumber = formData.vatnumber.trim();
    if (formData.phone) inputParams.phone = formData.phone.trim();

    await this.userService
      .createUserWithRole(inputParams)
      .subscribe({
        next: (response) => {
          this.alertService.alert("Customer created successfully!", 'success');
          this.dialogRef.close({
            status: true,
            customer: response
          });
        },
        error: (error) => {
          this.alertService.alert(error.error.message, 'error');
          this.dialogRef.close({
            status: false,
          });
        },
      });
  }

}
